import React, { useState } from "react";
import {
    useTranslation,
    useLocalNotificationBanner,
} from "Shared/util/custom-hooks";
import { saveAnnualKycFollowUpAnswers } from "Shared/util/web-api";
import { CustomerAnnualKycFollowUp } from "Shared/util/types";
import useCustomerContext from "Contexts/customer-context";
import { toggleImbox } from "Shared/util/imbox-api";
import Page from "Shared/components/page/page";
import Description from "Shared/components/description";
import { RadioGroup } from "Shared/components/radio-group";
import Button from "Shared/components/button";
import FormCard, {
    FormCardStatusTypes,
} from "Shared/components/cards/form-card/form-card";
import TextLink from "Shared/components/text-link";
import styles from "./annual-kyc-follow-up-page.css";

type AnnualKycFollowUpAnswers = {
    beneficialOwnersAnswer: boolean | null;
    boardMembersAnswer: boolean | null;
    pepAnswer: boolean | null;
};

const AnnualKycFollowUpPage = () => {
    const [isSubmittingAnswers, setIsSubmittingAnswers] = useState(false);
    const [answers, setAnswers] = useState<AnnualKycFollowUpAnswers>({
        beneficialOwnersAnswer: null,
        boardMembersAnswer: null,
        pepAnswer: null,
    });
    const t = useTranslation((x) => x.annual_kyc_follow_up);
    const { customerData, updateCustomerUpdateAnnualKycFollowUp } =
        useCustomerContext();
    const {
        NotificationBannerType,
        addLocalNotificationBanner,
        removeAllLocalNotificationBanners,
    } = useLocalNotificationBanner();

    const { beneficialOwnersAnswer, boardMembersAnswer, pepAnswer } = answers;

    const validAnswers =
        beneficialOwnersAnswer != null &&
        boardMembersAnswer != null &&
        pepAnswer != null;

    const handleSubmitAnswers = () => {
        if (!validAnswers) return;

        setIsSubmittingAnswers(true);
        removeAllLocalNotificationBanners();

        /* NOTE: React might show an incorrect error ("Warning: Can't perform a React state update on an unmounted component...") if component
    have been unmounted on "setState". This should be fixed in upcoming release https://github.com/facebook/react/pull/22114 */
        saveAnnualKycFollowUpAnswers({
            hasBeneficialOwnersChanged: beneficialOwnersAnswer,
            hasBoardMembersChanged: boardMembersAnswer,
            hasPepChanged: pepAnswer,
        })
            .then((result: CustomerAnnualKycFollowUp) => {
                updateCustomerUpdateAnnualKycFollowUp(result);
                addLocalNotificationBanner(
                    (tnb) => tnb.annual_kyc_follow_up_submit_succeeded,
                    NotificationBannerType.Success,
                );
            })
            .catch(() =>
                addLocalNotificationBanner(
                    (tnb) => tnb.a_technical_error_occurred,
                    NotificationBannerType.Error,
                ),
            )
            .finally(() => setIsSubmittingAnswers(false));
    };

    const handleBeneficialOwnersAnswerChanged = (value: string) =>
        setAnswers((prev) => ({
            ...prev,
            beneficialOwnersAnswer: value === true.toString(),
        }));
    const handleBoardMembersAnswerChanged = (value: string) =>
        setAnswers((prev) => ({
            ...prev,
            boardMembersAnswer: value === true.toString(),
        }));
    const handlePepAnswerChanged = (value: string) =>
        setAnswers((prev) => ({
            ...prev,
            pepAnswer: value === true.toString(),
        }));

    const questionOptions = [
        { label: t.questions.yes, value: true.toString() },
        { label: t.questions.no, value: false.toString() },
    ];
    const allowEdit =
        customerData?.annualKycFollowUp?.due ||
        customerData?.annualKycFollowUp?.passedDue;
    const allowSubmit = allowEdit && validAnswers && !isSubmittingAnswers;

    return (
        <Page
            title={{
                text: t.annual_kyc,
                reducedMarginBottom: true,
            }}
            narrowWidth
        >
            <Description
                text={
                    <>
                        {`${t.introduction_part_one} `}
                        <TextLink
                            text={t.introduction_part_two}
                            link={toggleImbox}
                        />
                        {` ${t.introduction_part_three}`}
                    </>
                }
                className={styles.introductionContainer}
            />
            <FormCard
                status={
                    boardMembersAnswer != null
                        ? FormCardStatusTypes.VALID
                        : FormCardStatusTypes.INVALID
                }
                header={t.questions.board_members_title}
                className={styles.formCardContainer}
                content={
                    <>
                        <RadioGroup
                            options={questionOptions}
                            selectedOption={boardMembersAnswer?.toString()}
                            onChange={handleBoardMembersAnswerChanged}
                            disabled={!allowEdit || isSubmittingAnswers}
                        />
                    </>
                }
            />
            <FormCard
                status={
                    beneficialOwnersAnswer != null
                        ? FormCardStatusTypes.VALID
                        : FormCardStatusTypes.INVALID
                }
                header={t.questions.beneficial_owners_title}
                className={styles.formCardContainer}
                content={
                    <>
                        <p className={styles.explanation}>
                            {t.questions.beneficial_owners_explanation}
                        </p>
                        <RadioGroup
                            options={questionOptions}
                            selectedOption={beneficialOwnersAnswer?.toString()}
                            onChange={handleBeneficialOwnersAnswerChanged}
                            disabled={!allowEdit || isSubmittingAnswers}
                        />
                    </>
                }
            />
            <FormCard
                status={
                    pepAnswer != null
                        ? FormCardStatusTypes.VALID
                        : FormCardStatusTypes.INVALID
                }
                header={t.questions.pep_title}
                className={styles.formCardContainer}
                content={
                    <>
                        <p className={styles.explanation}>
                            {t.questions.pep_explanation}
                        </p>
                        <RadioGroup
                            options={questionOptions}
                            selectedOption={pepAnswer?.toString()}
                            onChange={handlePepAnswerChanged}
                            disabled={!allowEdit || isSubmittingAnswers}
                        />
                    </>
                }
            />
            <Button
                buttonType="action-primary"
                text={t.submit_your_answers}
                onClick={handleSubmitAnswers}
                isLoading={isSubmittingAnswers}
                attributes={{ disabled: !allowSubmit }}
                className={styles.submitButton}
            />
        </Page>
    );
};

export default AnnualKycFollowUpPage;
